import { type MenuItem, type MenuCategory } from '../../../mocks/menuData';

interface MenuCategoryProps {
  category: MenuCategory;
  activeFilter: string;
  onItemClick: (item: MenuItem) => void;
}

function DietDot({ tags }: { tags: string[] }) {
  const isVeg = tags.includes('vegetarian') && !tags.includes('non-veg');
  const isNonVeg = tags.includes('non-veg');
  if (isVeg) return (
    <span className="w-5 h-5 flex items-center justify-center rounded-sm bg-white border border-green-600 flex-shrink-0">
      <span className="w-2.5 h-2.5 rounded-full bg-green-600 block"></span>
    </span>
  );
  if (isNonVeg) return (
    <span className="w-5 h-5 flex items-center justify-center rounded-sm bg-white border border-red-600 flex-shrink-0">
      <span className="w-2.5 h-2.5 rounded-full bg-red-600 block"></span>
    </span>
  );
  return null;
}

export default function MenuCategorySection({ category, activeFilter, onItemClick }: MenuCategoryProps) {
  const filteredItems = category.items.filter((item) => {
    if (activeFilter === 'all') return true;
    return item.tags.includes(activeFilter);
  });

  if (filteredItems.length === 0) return null;

  return (
    <section id={category.id} className="mb-16 scroll-mt-36">
      {/* Category Header */}
      <div className="flex items-center gap-4 mb-8">
        <div
          className="w-12 h-12 flex items-center justify-center rounded-2xl flex-shrink-0"
          style={{ backgroundColor: `${category.accent}20` }}
        >
          <i className={`${category.icon} text-2xl`} style={{ color: category.accent }}></i>
        </div>
        <div>
          <h2 className="font-serif text-3xl text-charcoal">{category.name}</h2>
          <p className="font-sans text-sm text-charcoal/50 mt-0.5">{category.description}</p>
        </div>
        <div
          className="hidden sm:block flex-1 h-px ml-4"
          style={{ background: `linear-gradient(to right, ${category.accent}50, transparent)` }}
        ></div>
      </div>

      {/* All items — uniform card grid */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
        {filteredItems.map((item) => (
          <button
            key={item.id}
            onClick={() => onItemClick(item)}
            className="group text-left rounded-2xl overflow-hidden cursor-pointer hover:-translate-y-1 transition-all duration-300 bg-white border border-charcoal/5 flex flex-col"
            style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}
          >
            {/* Image — fixed height, same on every card */}
            <div className="relative w-full overflow-hidden bg-charcoal/5" style={{ height: '160px' }}>
              {item.image ? (
                <img
                  src={item.image}
                  alt={item.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <i className="ri-restaurant-2-line text-4xl text-charcoal/20"></i>
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
              {/* Veg / Non-veg dot — top-left of image */}
              <div className="absolute top-2.5 left-2.5">
                <DietDot tags={item.tags} />
              </div>
              {/* Spice badge — top-right */}
              {item.spiceLevel >= 4 && (
                <div className="absolute top-2.5 right-2.5 bg-coral/90 text-white rounded-full px-1.5 py-0.5 flex items-center gap-0.5">
                  <i className="ri-fire-fill text-[10px]"></i>
                  <span className="font-sans text-[9px] font-bold">Hot</span>
                </div>
              )}
            </div>

            {/* Card body — consistent padding and layout */}
            <div className="p-3 sm:p-4 flex flex-col flex-1">
              {/* Name + Price */}
              <div className="flex items-start justify-between gap-1.5 mb-1.5">
                <h3 className="font-sans font-bold text-sm sm:text-base text-charcoal leading-tight flex-1">
                  {item.name}
                </h3>
                <span
                  className="font-sans font-bold text-sm whitespace-nowrap flex-shrink-0"
                  style={{ color: category.accent }}
                >
                  {item.priceFrom ? `From ${item.price}` : item.price}
                </span>
              </div>

              {/* Description — 2 lines, same on every card */}
              <p className="font-sans text-xs text-charcoal/55 leading-relaxed line-clamp-2 flex-1">
                {item.description}
              </p>

              {/* Footer row */}
              <div className="flex items-center gap-1.5 mt-2.5 flex-wrap">
                {item.spiceLevel > 0 && item.spiceLevel < 4 && (
                  <div className="flex items-center gap-0.5">
                    {[...Array(Math.min(item.spiceLevel, 3))].map((_, i) => (
                      <i key={i} className="ri-fire-fill text-[10px] text-coral"></i>
                    ))}
                  </div>
                )}
                {item.proteins && (
                  <span className="font-sans text-[10px] text-saffron bg-saffron/10 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                    {item.proteins.length} proteins
                  </span>
                )}
                {item.tags.includes('vegan') && (
                  <span className="font-sans text-[10px] text-green-700 bg-green-50 px-1.5 py-0.5 rounded-full whitespace-nowrap flex items-center gap-0.5">
                    <i className="ri-leaf-line text-[9px]"></i> Vegan
                  </span>
                )}
              </div>
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}
